import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { ApiRequestError } from './api-client';

/**
 * One QueryClient for the app.
 *
 * Failed *mutations* surface a toast automatically - an ERP user who clicks Save
 * must always learn whether it worked. Failed *queries* do not, because screens
 * render their own ErrorState with a retry affordance in place of the content.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error) => {
        // Auth, permission and validation failures will not fix themselves.
        if (error instanceof ApiRequestError && error.status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Only complain about background refetch failures once data is on screen.
      if (query.state.data !== undefined && error instanceof ApiRequestError) {
        toast.error('Could not refresh this data', { description: error.message });
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      if (!(error instanceof ApiRequestError)) {
        toast.error('Something went wrong', { description: 'Please try again.' });
        return;
      }
      // Field-level validation is rendered inline on the form, not as a toast.
      if (error.code === 'VALIDATION_ERROR' && error.details?.length) return;
      if (error.isAuthError) return;

      toast.error(error.code === 'FORBIDDEN' ? 'Not permitted' : 'Could not save your changes', {
        description: error.message,
      });
    },
  }),
});

/**
 * Query key factory. Centralising keys keeps invalidation honest: a mutation
 * invalidates `queryKeys.leads.all` and every list, detail and count under it
 * refreshes, with no string literals scattered across features.
 */
export const queryKeys = {
  auth: {
    me: ['auth', 'me'] as const,
    sessions: ['auth', 'sessions'] as const,
  },
  dashboard: {
    all: ['dashboard'] as const,
    summary: (params: object) => ['dashboard', 'summary', params] as const,
    mySummary: (params: object) => ['dashboard', 'my-summary', params] as const,
    /** The seven operational boards. */
    boards: ['dashboard', 'boards'] as const,
    board: (key: string, params: object) => ['dashboard', 'board', key, params] as const,
  },
  leads: {
    all: ['leads'] as const,
    list: (params: object) => ['leads', 'list', params] as const,
    summary: (params: object) => ['leads', 'summary', params] as const,
    detail: (id: string) => ['leads', 'detail', id] as const,
    /** Static per tenant; the pickers share one cached copy. */
    lookups: ['leads', 'lookups'] as const,
    cities: (stateId?: string, search?: string) =>
      ['leads', 'cities', stateId ?? '', search ?? ''] as const,
    duplicates: (mobile: string, excludeId?: string) =>
      ['leads', 'duplicates', mobile, excludeId ?? ''] as const,
    activities: (id: string, params: object) => ['leads', id, 'activities', params] as const,
    documents: (id: string) => ['leads', id, 'documents'] as const,
    conversionPreview: (id: string) => ['leads', id, 'conversion-preview'] as const,
  },
  customers: {
    all: ['customers'] as const,
    list: (params: object) => ['customers', 'list', params] as const,
    summary: (params: object) => ['customers', 'summary', params] as const,
    detail: (id: string) => ['customers', 'detail', id] as const,
    /** Static per tenant; the pickers share one cached copy. */
    lookups: ['customers', 'lookups'] as const,
    cities: (stateId?: string, search?: string) =>
      ['customers', 'cities', stateId ?? '', search ?? ''] as const,
    duplicates: (mobile?: string, email?: string, excludeId?: string) =>
      ['customers', 'duplicates', mobile ?? '', email ?? '', excludeId ?? ''] as const,
    contacts: (id: string) => ['customers', id, 'contacts'] as const,
    addresses: (id: string) => ['customers', id, 'addresses'] as const,
    projects: (id: string) => ['customers', id, 'projects'] as const,
    quotations: (id: string) => ['customers', id, 'quotations'] as const,
    invoices: (id: string) => ['customers', id, 'invoices'] as const,
    financials: (id: string) => ['customers', id, 'financials'] as const,
    subsidies: (id: string) => ['customers', id, 'subsidies'] as const,
    leads: (id: string) => ['customers', id, 'leads'] as const,
    activities: (id: string, params: object) => ['customers', id, 'activities', params] as const,
    documents: (id: string) => ['customers', id, 'documents'] as const,
  },
  siteSurveys: {
    all: ['site-surveys'] as const,
    list: (params: object) => ['site-surveys', 'list', params] as const,
    summary: (params: object) => ['site-surveys', 'summary', params] as const,
    detail: (id: string) => ['site-surveys', 'detail', id] as const,
    /** Static per tenant; the pickers share one cached copy. */
    lookups: ['site-surveys', 'lookups'] as const,
    subjects: (search?: string) => ['site-surveys', 'subjects', search ?? ''] as const,
    sites: (customerId: string) => ['site-surveys', 'sites', customerId] as const,
    photos: (id: string) => ['site-surveys', id, 'photos'] as const,
    documents: (id: string) => ['site-surveys', id, 'documents'] as const,
  },
  quotations: {
    all: ['quotations'] as const,
    list: (params: object) => ['quotations', 'list', params] as const,
    summary: (params: object) => ['quotations', 'summary', params] as const,
    detail: (id: string) => ['quotations', 'detail', id] as const,
    /** Static per tenant; the form pickers share one cached copy. */
    lookups: ['quotations', 'lookups'] as const,
    parties: (search?: string) => ['quotations', 'parties', search ?? ''] as const,
    addresses: (customerId: string) => ['quotations', 'addresses', customerId] as const,
    surveys: (customerId?: string, leadId?: string) =>
      ['quotations', 'surveys', customerId ?? '', leadId ?? ''] as const,
    products: (search?: string, category?: string) =>
      ['quotations', 'products', search ?? '', category ?? ''] as const,
    versions: (id: string) => ['quotations', id, 'versions'] as const,
  },
  projects: {
    all: ['projects'] as const,
    list: (params: object) => ['projects', 'list', params] as const,
    summary: (params: object) => ['projects', 'summary', params] as const,
    detail: (id: string) => ['projects', 'detail', id] as const,
    /** Static per tenant: the pipeline, the staff, the next number. */
    lookups: ['projects', 'lookups'] as const,
    workOrders: (search?: string) => ['projects', 'work-orders', search ?? ''] as const,
    timeline: (id: string, params: object) => ['projects', id, 'timeline', params] as const,
    journey: (id: string, params: object) => ['projects', id, 'journey', params] as const,
    documents: (id: string) => ['projects', id, 'documents'] as const,
    financials: (id: string) => ['projects', id, 'financials'] as const,
    dispatches: (id: string) => ['projects', id, 'dispatches'] as const,
    dispatch: (id: string, dispatchId: string) => ['projects', id, 'dispatches', dispatchId] as const,
    dispatchableStock: (id: string, warehouseId: string) =>
      ['projects', id, 'dispatchable', warehouseId] as const,
    closure: (id: string) => ['projects', id, 'closure'] as const,
  },

  /* ---------------------------------------------------------------- */
  /* EPC execution                                                     */
  /* ---------------------------------------------------------------- */

  systemDesigns: {
    all: ['system-designs'] as const,
    list: (params: object) => ['system-designs', 'list', params] as const,
    summary: (params: object) => ['system-designs', 'summary', params] as const,
    detail: (id: string) => ['system-designs', 'detail', id] as const,
    lookups: ['system-designs', 'lookups'] as const,
    fromSurvey: (surveyId: string) => ['system-designs', 'from-survey', surveyId] as const,
  },
  customerApprovals: {
    all: ['customer-approvals'] as const,
    list: (params: object) => ['customer-approvals', 'list', params] as const,
    summary: (params: object) => ['customer-approvals', 'summary', params] as const,
    detail: (id: string) => ['customer-approvals', 'detail', id] as const,
    quotations: ['customer-approvals', 'quotations'] as const,
  },
  materialPlans: {
    all: ['material-plans'] as const,
    list: (params: object) => ['material-plans', 'list', params] as const,
    summary: (params: object) => ['material-plans', 'summary', params] as const,
    detail: (id: string) => ['material-plans', 'detail', id] as const,
    lookups: ['material-plans', 'lookups'] as const,
  },
  inventory: {
    all: ['inventory'] as const,
    dashboard: (params: object) => ['inventory', 'dashboard', params] as const,
    stock: (params: object) => ['inventory', 'stock', params] as const,
    statement: (params: object) => ['inventory', 'statement', params] as const,
    transactions: (params: object) => ['inventory', 'transactions', params] as const,
    lowStock: (params: object) => ['inventory', 'low-stock', params] as const,
    batches: (params: object) => ['inventory', 'batches', params] as const,
    serials: (params: object) => ['inventory', 'serials', params] as const,
    lookups: ['inventory', 'lookups'] as const,
  },
  stockAllocations: {
    all: ['stock-allocations'] as const,
    list: (params: object) => ['stock-allocations', 'list', params] as const,
    summary: (params: object) => ['stock-allocations', 'summary', params] as const,
    detail: (id: string) => ['stock-allocations', 'detail', id] as const,
  },
  stockTransfers: {
    all: ['stock-transfers'] as const,
    list: (params: object) => ['stock-transfers', 'list', params] as const,
    summary: (params: object) => ['stock-transfers', 'summary', params] as const,
    detail: (id: string) => ['stock-transfers', 'detail', id] as const,
  },
  stockAdjustments: {
    all: ['stock-adjustments'] as const,
    list: (params: object) => ['stock-adjustments', 'list', params] as const,
    summary: (params: object) => ['stock-adjustments', 'summary', params] as const,
    detail: (id: string) => ['stock-adjustments', 'detail', id] as const,
  },
  vendors: {
    all: ['vendors'] as const,
    list: (params: object) => ['vendors', 'list', params] as const,
    detail: (id: string) => ['vendors', 'detail', id] as const,
    activity: (id: string) => ['vendors', 'activity', id] as const,
    lookups: ['vendors', 'lookups'] as const,
  },
  purchaseRequisitions: {
    all: ['purchase-requisitions'] as const,
    list: (params: object) => ['purchase-requisitions', 'list', params] as const,
    summary: (params: object) => ['purchase-requisitions', 'summary', params] as const,
    detail: (id: string) => ['purchase-requisitions', 'detail', id] as const,
    departments: ['purchase-requisitions', 'departments'] as const,
  },
  purchaseOrders: {
    all: ['purchase-orders'] as const,
    list: (params: object) => ['purchase-orders', 'list', params] as const,
    summary: (params: object) => ['purchase-orders', 'summary', params] as const,
    detail: (id: string) => ['purchase-orders', 'detail', id] as const,
    receivable: (id: string) => ['purchase-orders', 'receivable', id] as const,
    lookups: ['purchase-orders', 'lookups'] as const,
  },
  goodsReceipts: {
    all: ['goods-receipts'] as const,
    list: (params: object) => ['goods-receipts', 'list', params] as const,
    summary: (params: object) => ['goods-receipts', 'summary', params] as const,
    detail: (id: string) => ['goods-receipts', 'detail', id] as const,
  },
  purchaseReturns: {
    all: ['purchase-returns'] as const,
    list: (params: object) => ['purchase-returns', 'list', params] as const,
    summary: (params: object) => ['purchase-returns', 'summary', params] as const,
    detail: (id: string) => ['purchase-returns', 'detail', id] as const,
    vendorReceipts: (supplierId: string) =>
      ['purchase-returns', 'vendor-receipts', supplierId] as const,
  },
  finance: {
    all: ['finance'] as const,
    dashboard: ['finance', 'dashboard'] as const,
    receivables: (params: object) => ['finance', 'receivables', params] as const,
    receivablesSummary: ['finance', 'receivables', 'summary'] as const,
    payables: (params: object) => ['finance', 'payables', params] as const,
    payablesSummary: ['finance', 'payables', 'summary'] as const,
  },
  salesInvoices: {
    all: ['sales-invoices'] as const,
    list: (params: object) => ['sales-invoices', 'list', params] as const,
    summary: (params: object) => ['sales-invoices', 'summary', params] as const,
    detail: (id: string) => ['sales-invoices', 'detail', id] as const,
  },
  payments: {
    all: ['payments'] as const,
    list: (params: object) => ['payments', 'list', params] as const,
    summary: (params: object) => ['payments', 'summary', params] as const,
    detail: (id: string) => ['payments', 'detail', id] as const,
  },
  expenses: {
    all: ['expenses'] as const,
    list: (params: object) => ['expenses', 'list', params] as const,
    summary: (params: object) => ['expenses', 'summary', params] as const,
    detail: (id: string) => ['expenses', 'detail', id] as const,
    categories: ['expenses', 'categories'] as const,
  },
  hr: {
    all: ['hr'] as const,
    dashboard: ['hr', 'dashboard'] as const,
  },
  employees: {
    all: ['employees'] as const,
    list: (params: object) => ['employees', 'list', params] as const,
    detail: (id: string) => ['employees', 'detail', id] as const,
    lookups: ['employees', 'lookups'] as const,
    cities: (stateId: string) => ['employees', 'cities', stateId] as const,
  },
  attendance: {
    all: ['attendance'] as const,
    list: (params: object) => ['attendance', 'list', params] as const,
    summary: (params: object) => ['attendance', 'summary', params] as const,
    roster: (params: object) => ['attendance', 'roster', params] as const,
    detail: (id: string) => ['attendance', 'detail', id] as const,
    departments: ['attendance', 'departments'] as const,
  },
  leaves: {
    all: ['leaves'] as const,
    list: (params: object) => ['leaves', 'list', params] as const,
    summary: ['leaves', 'summary'] as const,
    detail: (id: string) => ['leaves', 'detail', id] as const,
    types: ['leaves', 'types'] as const,
    balances: (params: object) => ['leaves', 'balances', params] as const,
  },
  payroll: {
    all: ['payroll'] as const,
    list: (params: object) => ['payroll', 'list', params] as const,
    summary: ['payroll', 'summary'] as const,
    detail: (id: string) => ['payroll', 'detail', id] as const,
  },
  employeeTasks: {
    all: ['employee-tasks'] as const,
    list: (params: object) => ['employee-tasks', 'list', params] as const,
    summary: (params: object) => ['employee-tasks', 'summary', params] as const,
    detail: (id: string) => ['employee-tasks', 'detail', id] as const,
    projects: ['employee-tasks', 'projects'] as const,
  },
  performance: {
    all: ['performance'] as const,
    list: (params: object) => ['performance', 'list', params] as const,
    detail: (id: string) => ['performance', 'detail', id] as const,
    metrics: (params: object) => ['performance', 'metrics', params] as const,
    reviewers: ['performance', 'reviewers'] as const,
  },
  integrations: {
    all: ['integrations'] as const,
    status: ['integrations', 'status'] as const,
    summary: (provider: string) => ['integrations', 'summary', provider] as const,
    settings: (provider: string) => ['integrations', 'settings', provider] as const,
    queue: (params: object) => ['integrations', 'queue', params] as const,
    mappings: (params: object) => ['integrations', 'mappings', params] as const,
    logs: (params: object) => ['integrations', 'logs', params] as const,
    // The framework screens. All under the same `all` prefix, so one
    // invalidation after a connection change refreshes every view of it.
    providers: ['integrations', 'providers'] as const,
    connection: (provider: string) => ['integrations', 'connection', provider] as const,
    webhooks: (params: object) => ['integrations', 'webhooks', params] as const,
    webhook: (id: string) => ['integrations', 'webhook', id] as const,
    errors: (params: object) => ['integrations', 'errors', params] as const,
    errorCounts: ['integrations', 'error-counts'] as const,
    templates: ['integrations', 'templates'] as const,
    paymentOrders: (params: object) => ['integrations', 'payment-orders', params] as const,
    paymentOrder: (id: string) => ['integrations', 'payment-order', id] as const,
  },
  fabrications: {
    all: ['fabrications'] as const,
    list: (params: object) => ['fabrications', 'list', params] as const,
    summary: (params: object) => ['fabrications', 'summary', params] as const,
    detail: (id: string) => ['fabrications', 'detail', id] as const,
    assignees: ['fabrications', 'assignees'] as const,
  },
  electricalTests: {
    all: ['electrical-tests'] as const,
    list: (params: object) => ['electrical-tests', 'list', params] as const,
    summary: (params: object) => ['electrical-tests', 'summary', params] as const,
    detail: (id: string) => ['electrical-tests', 'detail', id] as const,
    engineers: ['electrical-tests', 'engineers'] as const,
  },
  installations: {
    all: ['installations'] as const,
    list: (params: object) => ['installations', 'list', params] as const,
    summary: (params: object) => ['installations', 'summary', params] as const,
    detail: (id: string) => ['installations', 'detail', id] as const,
    projectContext: (projectId: string) =>
      ['installations', 'project', projectId, 'context'] as const,
  },
  subsidies: {
    all: ['subsidies'] as const,
    list: (params: object) => ['subsidies', 'list', params] as const,
    summary: (params: object) => ['subsidies', 'summary', params] as const,
    detail: (id: string) => ['subsidies', 'detail', id] as const,
    stages: ['subsidies', 'stages'] as const,
  },
  netMeters: {
    all: ['net-meters'] as const,
    list: (params: object) => ['net-meters', 'list', params] as const,
    summary: (params: object) => ['net-meters', 'summary', params] as const,
    detail: (id: string) => ['net-meters', 'detail', id] as const,
    discoms: ['net-meters', 'discoms'] as const,
    projectApplications: (projectId: string) =>
      ['net-meters', 'project', projectId, 'applications'] as const,
  },
  discomInspections: {
    all: ['discom-inspections'] as const,
    list: (params: object) => ['discom-inspections', 'list', params] as const,
    summary: (params: object) => ['discom-inspections', 'summary', params] as const,
    detail: (id: string) => ['discom-inspections', 'detail', id] as const,
    projectContext: (projectId: string) =>
      ['discom-inspections', 'project', projectId, 'context'] as const,
  },
  projectClosures: {
    all: ['project-closures'] as const,
    list: (params: object) => ['project-closures', 'list', params] as const,
    summary: (params: object) => ['project-closures', 'summary', params] as const,
    detail: (id: string) => ['project-closures', 'detail', id] as const,
    byProject: (projectId: string) => ['project-closures', 'project', projectId] as const,
  },
  warranties: {
    all: ['warranties'] as const,
    list: (params: object) => ['warranties', 'list', params] as const,
    summary: (params: object) => ['warranties', 'summary', params] as const,
    detail: (id: string) => ['warranties', 'detail', id] as const,
    projectHistory: (projectId: string) => ['warranties', 'history', 'project', projectId] as const,
    customerHistory: (customerId: string) =>
      ['warranties', 'history', 'customer', customerId] as const,
  },
  serviceTickets: {
    all: ['service-tickets'] as const,
    list: (params: object) => ['service-tickets', 'list', params] as const,
    summary: (params: object) => ['service-tickets', 'summary', params] as const,
    detail: (id: string) => ['service-tickets', 'detail', id] as const,
    visits: (params: object) => ['service-tickets', 'visits', params] as const,
    lookups: ['service-tickets', 'lookups'] as const,
    coverage: (projectId: string) => ['service-tickets', 'coverage', projectId] as const,
    dashboard: (params: object) => ['service-tickets', 'dashboard', params] as const,
  },
  amcContracts: {
    all: ['amc-contracts'] as const,
    list: (params: object) => ['amc-contracts', 'list', params] as const,
    summary: (params: object) => ['amc-contracts', 'summary', params] as const,
    detail: (id: string) => ['amc-contracts', 'detail', id] as const,
    visits: (params: object) => ['amc-contracts', 'visits', params] as const,
  },
  // The company's own record. One prefix for the profile, branches, numbering
  // and preferences: they are four views of one settings screen, and promoting a
  // branch to head office changes what the profile section says about the
  // registered place of business.
  company: {
    all: ['company'] as const,
    profile: ['company', 'profile'] as const,
    branches: (includeInactive: boolean) => ['company', 'branches', includeInactive] as const,
    numberSeries: ['company', 'number-series'] as const,
    preferences: ['company', 'preferences'] as const,
  },
  users: {
    all: ['users'] as const,
    list: (params: object) => ['users', 'list', params] as const,
    detail: (id: string) => ['users', 'detail', id] as const,
    assignable: (search?: string) => ['users', 'assignable', search ?? ''] as const,
  },
  roles: {
    all: ['roles'] as const,
    list: (params: object) => ['roles', 'list', params] as const,
    detail: (id: string) => ['roles', 'detail', id] as const,
    permissionCatalog: ['roles', 'permission-catalog'] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    list: (params: object) => ['notifications', 'list', params] as const,
    unreadCount: ['notifications', 'unread-count'] as const,
  },
  // The Communication Centre. The log, its summary and the template catalogue
  // share one prefix on purpose: sending a message changes all three at once,
  // and a narrower rule would leave a summary contradicting the rows beneath it.
  communications: {
    all: ['communications'] as const,
    list: (params: object) => ['communications', 'list', params] as const,
    summary: (params: object) => ['communications', 'summary', params] as const,
    detail: (id: string) => ['communications', 'detail', id] as const,
    templates: ['communications', 'templates'] as const,
  },
  auditLogs: {
    all: ['audit-logs'] as const,
    list: (params: object) => ['audit-logs', 'list', params] as const,
    forEntity: (entityType: string, entityId: string) =>
      ['audit-logs', 'entity', entityType, entityId] as const,
    entityTypes: ['audit-logs', 'entity-types'] as const,
  },
  reports: {
    all: ['reports'] as const,
    catalogue: ['reports', 'catalogue'] as const,
    run: (key: string, params: object) => ['reports', 'run', key, params] as const,
  },
} as const;
